import { ComponentProps, ReactNode, type JSX } from 'react';
import { useIntl } from 'react-intl';
import type { Person } from 'schema-dts';
import Avatar from '@alltrails/denali/components/Avatar';
import { type User } from '@alltrails/types/User';
import { User as BelayUser } from '@alltrails/types/belay';
import { fullName } from '@alltrails/shared/utils/formatters/textFormatters';
import { JsonLdScript } from '@alltrails/core';
import EntityLabel from '../EntityLabel';
import styles from './UserLabel.module.scss';

export type UserLabelProps = {
  baseUrl?: string;
  className?: string;
  eagerLoadImage?: boolean;
  hasNamePrefix?: boolean;
  nameExtension?: ReactNode;
  onClick?: () => void;
  secondaryText?: ComponentProps<typeof EntityLabel>['secondaryText'];
  tertiaryText?: ComponentProps<typeof EntityLabel>['tertiaryText'];
  testIdPrefix?: string;
  user: User | BelayUser;
  userLabelClassName?: string;
  secondaryTextClassName?: string;
  userConnectionLink?: JSX.Element;
  privacyElement?: JSX.Element;
  jsonLdPersonId?: string;
  ratingValue?: number;
  hasRawTitle?: boolean;
};

const UserLabel = ({
  baseUrl,
  className,
  eagerLoadImage,
  hasNamePrefix,
  nameExtension,
  onClick,
  secondaryText,
  tertiaryText,
  testIdPrefix,
  user,
  userLabelClassName,
  secondaryTextClassName,
  userConnectionLink,
  privacyElement,
  ratingValue,
  jsonLdPersonId,
  hasRawTitle
}: UserLabelProps): JSX.Element => {
  const intl = useIntl();
  const userFullName = fullName(user);
  const href = `/members/${user.slug}`;
  const testIdPrefixWithName = testIdPrefix ? `${testIdPrefix}-${userFullName}` : userFullName;
  const title = intl.formatMessage({ defaultMessage: 'By {name}' }, { name: userFullName });

  return (
    <>
      {jsonLdPersonId && (
        <JsonLdScript<Person>
          data={{
            '@type': 'Person',
            '@id': jsonLdPersonId,
            name: userFullName,
            url: href
          }}
        />
      )}
      <EntityLabel
        avatar={
          <Avatar
            className={styles.avatar}
            baseUrl={baseUrl}
            eager={eagerLoadImage}
            linkInfo={{ href, target: '_blank', rel: ['nofollow'] }}
            onClick={onClick}
            testId={`${testIdPrefix}-image`}
            userId={user.id as number}
            userName={userFullName}
          />
        }
        className={className}
        link={{ href, rel: ['nofollow'] }}
        nameExtension={nameExtension}
        onClick={onClick}
        secondaryText={secondaryText}
        secondaryTextClassName={secondaryTextClassName}
        socialLink={userConnectionLink}
        tertiaryText={tertiaryText}
        testIdPrefix={testIdPrefixWithName}
        title={hasNamePrefix ? title : userFullName}
        titleClassName={userLabelClassName}
        privacyElement={privacyElement}
        ratingValue={ratingValue}
        hasRawTitle={hasRawTitle}
      />
    </>
  );
};

export default UserLabel;
